// Single-letter labels for ModuleExit handles, designed to be readable inside
// the small connector dots. Mapping is based on every ModuleExit.Name currently
// present in the database (22 modules / 34 unique names) — verified to be
// conflict-free per module so two exits on the same node never share a letter.
//
//   Per-module exit sets:
//     Calendar:                 CHECKED, NOT_CHECKED                   -> C, U
//     Call Forward (Agent):     ERROR, NO_CONNECTION, SUCCES, HANGUP   -> E, X, S, H
//     Call Forward (Free):      NO CONNECTION, SUCCES                  -> X, S
//     Call Forward (Huntgroup): SUCCES, ABORT, NO_AGENT_AVAILABLE,
//                               QUEUE_FULL, ERROR, HANGUP              -> S, A, N, Q, E, H
//     Compare:                  TRUE, FALSE                            -> Y, N
//     Data Lookup:              FOUND, NOT_FOUND, HANGUP               -> F, N, H
//     Input:                    ERROR, SUCCES, HANGUP                  -> E, S, H
//     Jump Application:         DEFAULT                                 -> D
//     Menu:                     DTMF_0..9 / *, # / UNDEFINED /
//                               TIME_OUT / ERROR / HANGUP              -> 0..9, *, #, ?, T, E, H
//     Message Send:             DEFAULT                                 -> D
//     Play / Play Variable:     DEFAULT, HANGUP                        -> D, H
//     Record:                   TOO_SHORT, DEFAULT                     -> <, D
//     Save Variable(s):         DEFAULT                                 -> D
//     Schedule Lookup:          FOUND, NOT_FOUND, HANGUP               -> F, N, H
//     Set Variable:             DEFAULT                                 -> D
//     Time of day greeting:     MORNING, AFTERNOON, DEFAULT, HANGUP    -> M, P, D, H
//     Trengo WhatsApp:          SUCCES, ERROR                          -> S, E
//     Voicemail:                NOT_ENABLED, SUCCES, TOO_SHORT, HANGUP -> !, S, <, H
//     Web Response:             DEFAULT, NO_QUESTIONS_LEFT, HANGUP     -> D, Q, H
//     Weekplan:                 CHECKED, NOT_CHECKED                   -> C, U
//
// Heuristics for unknown / future exit names:
//   1. DTMF_<digit>      -> the digit
//   2. DTMF_STAR / POUND -> * / #
//   3. Otherwise: first letter of the name, uppercased.

const STATIC_MAP: Record<string, string> = {
  ABORT: 'A',
  AFTERNOON: 'P', // P for PM
  CHECKED: 'C',
  DEFAULT: 'D',
  DTMF_0: '0',
  DTMF_1: '1',
  DTMF_2: '2',
  DTMF_3: '3',
  DTMF_4: '4',
  DTMF_5: '5',
  DTMF_6: '6',
  DTMF_7: '7',
  DTMF_8: '8',
  DTMF_9: '9',
  DTMF_POUND: '#',
  DTMF_STAR: '*',
  ERROR: 'E',
  FALSE: 'N', // N for "No"
  FOUND: 'F',
  HANGUP: 'H',
  MORNING: 'M',
  NO_CONNECTION: 'X',
  'NO CONNECTION': 'X',
  NO_AGENT_AVAILABLE: 'N', // only co-occurs with QUEUE_FULL/ABORT/etc., never NOT_FOUND/FALSE
  NO_QUESTIONS_LEFT: 'Q',
  NOT_CHECKED: 'U', // U for Unchecked
  NOT_ENABLED: '!',
  NOT_FOUND: 'N', // only co-occurs with FOUND/HANGUP
  QUEUE_FULL: 'Q',
  SUCCES: 'S',
  SUCCESS: 'S',
  TIME_OUT: 'T',
  TIMEOUT: 'T',
  TOO_SHORT: '<',
  TRUE: 'Y', // Y for "Yes"
  UNDEFINED: '?',
};

export function exitLetter(name: string | null | undefined): string {
  if (!name) return '?';
  const key = name.trim().toUpperCase();
  if (STATIC_MAP[key]) return STATIC_MAP[key];
  // DTMF_<digit>
  const dtmf = /^DTMF[_-]?([0-9])$/.exec(key);
  if (dtmf) return dtmf[1];
  if (key === 'DTMF_*' || key === 'DTMF*') return '*';
  if (key === 'DTMF_#' || key === 'DTMF#') return '#';
  // Fallback: first alphanumeric char
  const ch = key.replace(/[^A-Z0-9]/g, '')[0];
  return ch || '?';
}

// Human-readable form for tooltips: "NO_AGENT_AVAILABLE" -> "No agent available".
export function exitLabel(name: string | null | undefined): string {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/[_]+/g, ' ')
    .replace(/\bdtmf\b/g, 'DTMF')
    .replace(/^./, (c) => c.toUpperCase());
}
